import { useCallback, useEffect, useRef, useState } from "react";
import { getApiErrorMessage } from "../../../lib/result-codes";
import { getMerchantList } from "../api/merchant.api";
import {
  defaultMerchantFilter,
  type GetMerchantListRequest,
  type MerchantFilters,
  type MerchantListItem,
} from "../types/merchant.types";

export interface UseGetMerchantListRequest {
  search: string;
  filters: MerchantFilters;
  pagination: {
    page: number;
    totalCount: number;
  };
  sorting?: {
    sort_key?: string;
    sort_direction?: "asc" | "desc";
  };
}

interface UseGetMerchantListReturn {
  merchants: MerchantListItem[];
  isLoading: boolean;
  isError: boolean;
  error: string | null;
  refreshMerchants: () => Promise<void>;
  fetchMerchant: () => Promise<void>;
  request: UseGetMerchantListRequest;
  haveFilter: boolean;
  setFilter: (filters: MerchantFilters | ((prev: MerchantFilters) => MerchantFilters)) => void;
  setPagination: (
    pagination:
      | UseGetMerchantListRequest["pagination"]
      | ((
          prev: UseGetMerchantListRequest["pagination"],
        ) => UseGetMerchantListRequest["pagination"]),
  ) => void;
  setSorting: (sorting: UseGetMerchantListRequest["sorting"]) => void;
  hasInitialData: boolean;
}

const defaultRequest: UseGetMerchantListRequest = {
  search: "",
  filters: defaultMerchantFilter,
  pagination: {
    page: 1,
    totalCount: 0,
  },
};

export const toListRequest = (
  filters: MerchantFilters,
  search: string,
  pagination: { index: number; size: number },
  sorting: UseGetMerchantListRequest["sorting"],
): GetMerchantListRequest => {
  const keyword = filters.keyword.trim() || search.trim();
  return {
    ...(keyword ? { keyword } : {}),
    ...(filters.category_id ? { category_id: filters.category_id } : {}),
    ...(filters.verification_status === "verified"
      ? { verification_status: true as const }
      : filters.verification_status === "pending"
        ? { verification_status: false as const }
        : {}),
    ...(filters.badge_level ? { badge_level: filters.badge_level } : {}),
    ...(sorting?.sort_key ? { sort_key: sorting.sort_key } : {}),
    ...(sorting?.sort_direction ? { sort_direction: sorting.sort_direction } : {}),
    index: pagination.index,
    size: pagination.size,
  };
};

const getHasActiveFilters = (source: MerchantFilters, search: string) =>
  source.keyword !== "" ||
  search !== "" ||
  source.category_id !== "" ||
  source.verification_status !== "" ||
  source.badge_level !== "";

export const useGetMerchantList = (): UseGetMerchantListReturn => {
  const [merchants, setMerchants] = useState<MerchantListItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [request, setRequest] = useState<UseGetMerchantListRequest>(defaultRequest);
  const [hasInitialData, setHasInitialData] = useState(false);
  const sessionRef = useRef(0);
  const requestRef = useRef(request);
  const skipNextAutoFetchRef = useRef(false);
  const itemsPerPage = 20;
  const haveFilter = getHasActiveFilters(request.filters, request.search);

  useEffect(() => {
    requestRef.current = request;
  }, [request]);

  const fetchMerchantWithRequest = useCallback(
    async (currentRequest: UseGetMerchantListRequest) => {
      sessionRef.current += 1;
      const currentSession = sessionRef.current;

      try {
        const hasActiveFilters = getHasActiveFilters(currentRequest.filters, currentRequest.search);

        if (hasActiveFilters) {
          setMerchants([]);
        }

        setIsLoading(true);
        setIsError(false);
        setError(null);

        const response = await getMerchantList(
          toListRequest(
            currentRequest.filters,
            currentRequest.search,
            {
              index: currentRequest.pagination.page - 1,
              size: itemsPerPage,
            },
            currentRequest.sorting,
          ),
        );

        if (currentSession !== sessionRef.current) return;

        setMerchants([...(response.list || [])]);
        setRequest((prev) => ({
          ...prev,
          pagination: {
            ...prev.pagination,
            totalCount: response.paging?.total || 0,
          },
        }));
        setHasInitialData(true);
      } catch (fetchError: unknown) {
        if (currentSession !== sessionRef.current) return;

        setIsError(true);
        setError(getApiErrorMessage(fetchError, "Failed to load merchants"));
      } finally {
        if (currentSession === sessionRef.current) {
          setIsLoading(false);
        }
      }
    },
    [],
  );

  const fetchMerchant = useCallback(async () => {
    const currentRequest = requestRef.current;
    await fetchMerchantWithRequest(currentRequest);
  }, [fetchMerchantWithRequest]);

  const refreshMerchants = useCallback(async () => {
    if (haveFilter) {
      const clearedRequest: UseGetMerchantListRequest = {
        ...defaultRequest,
        pagination: {
          ...requestRef.current.pagination,
          page: 1,
          totalCount: 0,
        },
      };

      skipNextAutoFetchRef.current = true;
      requestRef.current = clearedRequest;
      setRequest(clearedRequest);
      setMerchants([]);
      await fetchMerchantWithRequest(clearedRequest);
      return;
    }

    setMerchants([]);
    await fetchMerchant();
  }, [fetchMerchant, fetchMerchantWithRequest, haveFilter]);

  const setFilter = useCallback(
    (filters: MerchantFilters | ((prev: MerchantFilters) => MerchantFilters)) => {
      setRequest((prev) => ({
        ...prev,
        filters: typeof filters === "function" ? filters(prev.filters) : filters,
        pagination: {
          ...prev.pagination,
          page: 1,
        },
      }));
    },
    [],
  );

  const setPagination = useCallback(
    (
      pagination:
        | UseGetMerchantListRequest["pagination"]
        | ((
            prev: UseGetMerchantListRequest["pagination"],
          ) => UseGetMerchantListRequest["pagination"]),
    ) => {
      setRequest((prev) => ({
        ...prev,
        pagination: typeof pagination === "function" ? pagination(prev.pagination) : pagination,
      }));
    },
    [],
  );

  const setSorting = useCallback((sorting: UseGetMerchantListRequest["sorting"]) => {
    setRequest((prev) => ({
      ...prev,
      sorting,
      pagination: {
        ...prev.pagination,
        page: 1,
      },
    }));
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (skipNextAutoFetchRef.current) {
        skipNextAutoFetchRef.current = false;
        return;
      }
      fetchMerchant();
    }, 500);

    return () => clearTimeout(timer);
  }, [request.filters, request.search, request.pagination.page, request.sorting, fetchMerchant]);

  return {
    merchants,
    isLoading,
    isError,
    error,
    refreshMerchants,
    fetchMerchant,
    request,
    haveFilter,
    setFilter,
    setPagination,
    setSorting,
    hasInitialData,
  };
};
